<template>
  <div>
    <div v-if="notFound" class="container h-full w-full min-w-full">
      <div
        class="w-full pt-[200px] pb-[150px] max-w-[1200px] px-4 md:px-5 mx-auto relative z-3"
      >
        <h3 class="text-3xl text-center dark:text-white">
          Sorry, this profile does not exist.
        </h3>
        <h4 class="text-center mt-5">
          <a :href="baseUrl" class="dark:text-puertoRico"
            >NotedSource main page</a
          >
        </h4>
      </div>
    </div>
    <div v-if="profile" class="container h-full w-full min-w-full">
      <div
        id="metaTitle"
        class="hidden"
        v-html="
          `Work with ${profile.fullDisplayName || 'Experts'} | NotedSource`
        "
      ></div>
      <div
        id="metaDescription"
        class="hidden"
        v-html="
          profile.about ||
          profile.headline ||
          'Work with experts on NotedSource, a platform of thousands of knowledgeable PhDs, scientists, and industry experts'
        "
      ></div>
      <div
        id="metaCanonical"
        class="hidden"
        v-html="`${cvBaseUrl}/me/${$route.params.slugOrHashedId}`"
      ></div>
      <div class="min-h-screen bg-fixed bg-tiara dark:bg-blueDianne-1000">
        <div class="w-full bg-blueDianne border-b border-tiara-200">
          <div
            class="w-full pt-[80px] pb-[60px] max-w-[1200px] px-4 md:px-5 mx-auto relative z-3"
          >
            <div
              v-if="profile.fullDisplayName"
              class="flex flex-wrap lg:flex-nowrap px-4 items-center"
            >
              <div class="shrink-0 mx-auto lg:mx-0">
                <profile-image
                  v-if="profile"
                  border-color="australianMint"
                  :size="264"
                  :img-url="imageUrlFromProfile(profile)"
                />
              </div>
              <div
                class="text-center lg:text-left mx-auto lg:mx-0 mt-5 lg:mt-0 px-5 lg:ml-8"
              >
                <div>
                  <h1
                    class="text-48pt font-headline md:text-48pt text-white leading-[75px]"
                  >
                    <span>{{ profile.fullDisplayName }}</span>
                  </h1>
                </div>
                <div v-if="profile.headline">
                  <h1 class="text-2xl text-white leading-[40px] mt-3">
                    <span>{{ profile.headline }}</span>
                  </h1>
                </div>
                <client-only>
                  <create-collaboration-inquiry
                    :profile="profile"
                    :showing="showingInquireModal"
                  />
                </client-only>
                <div>
                  <div
                    v-if="profile.city || profile.state || profile.country"
                    class="font-headline inline-flex items-center text-sm leading-normal mt-2 mb-2 py-1 rounded-md text-white mt-5 italic"
                  >
                    <i class="fas fa-fw fa-map-marker-alt mr-2"></i>
                    <div>
                      <span
                        v-if="profile.city && profile.state && profile.country"
                      >
                        {{ profile.city }}, {{ profile.state[0] }},
                        {{ profile.country[0] }}
                      </span>
                      <span v-else-if="profile.city && profile.state">
                        {{ profile.city }}, {{ profile.state[0] }}
                      </span>
                      <span v-else-if="profile.state">
                        {{ profile.state[0] }}
                      </span>
                      <span v-else-if="profile.city">
                        {{ profile.city }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="profile.researchInterests?.length > 0"
          class="w-full bg-tiara dark:bg-blueDianne-900 pt-10 pb-3"
        >
          <div class="text-center">
            <h2
              class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline mb-2"
            >
              Research Expertise
            </h2>
          </div>
          <div
            class="max-w-[1200px] px-4 md:px-5 mx-auto text-center flex flex-wrap justify-center mb-3 relative"
          >
            <div
              v-for="researchInterest in profile.researchInterests"
              :key="researchInterest.hashedId"
              class="inline-block mx-1 my-1 highlighted-swipe yellow-swipe text-xl overflow-x-hidden text-ellipsis whitespace-nowrap dark:text-white"
            >
              {{ researchInterest.researchInterest }}
            </div>
          </div>
        </div>
        <div class="w-full max-w-[1200px] px-4 md:px-5 mx-auto">
          <div class="w-full flex flex-wrap">
            <div class="w-full md:w-1/2 md:pr-10">
              <section v-if="profile.about && profile.about.length > 0">
                <div
                  class="bg-white dark:bg-blueDianne-950 dark:text-white rounded-xl pb-5 pt-5 px-5 mt-10"
                >
                  <h2
                    class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline"
                  >
                    About
                  </h2>
                  <client-only>
                    <markdown-viewer
                      v-if="profile?.about"
                      :text="profile.about"
                    />
                  </client-only>
                  <div v-if="isSsr">
                    {{ profile.about }}
                  </div>
                </div>
              </section>

              <section
                v-if="
                  profile.semanticScholarAuthorId &&
                  profile.semanticScholarAuthorId !== 'not_found'
                "
              >
                <div
                  class="bg-white dark:bg-blueDianne-950 dark:text-white rounded-xl pb-5 pt-5 px-5 mt-10"
                >
                  <div class="flex justify-between items-center mb-4">
                    <h2
                      class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline"
                    >
                      Legacy Map
                    </h2>
                    <a
                      :href="`${webBaseUrl}/legacy-map/${profile.semanticScholarAuthorId}`"
                      target="_blank"
                      class="text-sm text-blueDianne dark:text-blueDianne-200 hover:text-blue-600 dark:hover:text-blue-300 font-bold flex items-center gap-2"
                    >
                      <i class="fas fa-external-link-alt"></i> Full View
                    </a>
                  </div>
                  <div
                    class="w-full relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 h-[600px] bg-gray-50 dark:bg-[#050a0c]"
                  >
                    <iframe
                      v-if="initialTheme !== null && !isPrerendering"
                      ref="legacyMapIframe"
                      :src="`${webBaseUrl}/legacy-map/${profile.semanticScholarAuthorId}?minimized=true&theme=${initialTheme}`"
                      class="absolute inset-0 w-full h-full border-0"
                      title="Author Legacy Map"
                    ></iframe>
                  </div>
                </div>
              </section>

              <section
                v-if="profile.publications && profile.publications.length > 0"
              >
                <div
                  class="flex flex-wrap bg-white dark:bg-blueDianne-950 dark:text-white rounded-xl pb-5 pt-5 px-5 mt-10"
                >
                  <h2
                    class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline mb-4"
                  >
                    Publications
                  </h2>
                  <div class="w-full flex flex-col gap-3">
                    <a
                      v-for="publication in profile.publications.filter(
                        (publication) => publication.title,
                      )"
                      :key="publication.hashedId"
                      :href="publication.url"
                      target="_blank"
                      class="block text-sm bg-gray-200/30 dark:bg-[#081014]/30 border border-gray-300 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 rounded-lg p-3 cursor-pointer transition-colors relative overflow-hidden group/paper"
                    >
                      <div
                        class="font-bold text-gray-800 dark:text-gray-200 leading-tight mb-1 group-hover/paper:text-blue-700 dark:group-hover/paper:text-blue-400 transition-colors"
                        v-html="publication.title"
                      ></div>
                      <div
                        class="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between items-center mt-2"
                      >
                        <span
                          class="truncate"
                          v-html="publication.containerTitle || 'Unknown Venue'"
                        ></span>
                        <div class="flex items-center gap-2 shrink-0">
                          <span
                            v-if="publication.publishedAt"
                            class="font-bold"
                          >
                            {{
                              $dayjs
                                .unix(publication.publishedAt.seconds)
                                .format("YYYY")
                            }}
                          </span>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
              </section>
            </div>
            <div class="w-full md:w-1/2">
              <section
                v-if="profile.educations && profile.educations.length > 0"
              >
                <div
                  class="flex flex-wrap bg-white dark:bg-blueDianne-950 dark:text-white rounded-xl pb-5 pt-5 px-5 mt-10"
                >
                  <h2
                    class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline"
                  >
                    Education
                  </h2>
                  <div
                    v-for="education in profile.educations"
                    :key="education.hashedId"
                    class="w-full mt-5"
                  >
                    <h3 class="font-medium uppercase">
                      {{ education.schoolName }}
                    </h3>
                    <div class="font-light">
                      <div>
                        <p
                          v-if="education.degree || education.specialization"
                          class="font-light"
                        >
                          <span v-if="education.degree">{{
                            education.degree
                          }}</span>
                          <span
                            v-if="education.degree && education.specialization"
                            >,
                          </span>
                          <span v-if="education.specialization">{{
                            education.specialization
                          }}</span>
                          <span
                            v-if="
                              (education.degree || education.specialization) &&
                              (education.graduationMonth ||
                                education.graduationYear)
                            "
                          >
                            /
                          </span>
                          <span v-if="education.graduationMonth">{{
                            education.graduationMonth[0]
                          }}</span>
                          <span
                            v-if="
                              education.graduationMonth &&
                              education.graduationYear
                            "
                            >,
                          </span>
                          <span v-if="education.graduationYear">{{
                            education.graduationYear
                          }}</span>
                          <span v-if="education.isComplete === false">
                            (anticipated)</span
                          >
                        </p>
                        <div
                          v-if="
                            education.schoolCity ||
                            education.schoolState ||
                            education.schoolCountry
                          "
                          class="flex items-center text-sm leading-normal mt-1 mb-2 font-headline italic"
                        >
                          <i
                            class="fas fa-fw fa-map-marker-alt mr-1 text-blueDianne dark:text-blueDianne-100"
                          ></i>
                          <div>
                            <span
                              v-if="
                                education.schoolCity &&
                                education.schoolState &&
                                education.schoolCountry
                              "
                            >
                              {{ education.schoolCity }},
                              {{ education.schoolState[0] }},
                              {{ education.schoolCountry[0] }}
                            </span>
                            <span
                              v-else-if="profile.city && education.schoolState"
                            >
                              {{ education.schoolCity }},
                              {{ education.schoolState[0] }}
                            </span>
                            <span v-else-if="education.schoolState">
                              {{ education.schoolState[0] }}
                            </span>
                            <span v-else-if="education.schoolCity">
                              {{ education.schoolCity }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section
                v-if="profile.experiences && profile.experiences.length > 0"
              >
                <div
                  class="flex flex-wrap bg-white dark:bg-blueDianne-950 dark:text-white rounded-xl pb-5 pt-5 px-5 mt-10"
                >
                  <h2
                    class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline"
                  >
                    Experience
                  </h2>
                  <div
                    v-for="experience in profile.experiences"
                    :key="experience.hashedId"
                    class="w-full mt-5"
                  >
                    <h3 class="font-medium uppercase">
                      {{ experience.companyName }}
                    </h3>
                    <div class="font-light">
                      <div>
                        <div
                          v-for="position in experience.positions"
                          :key="position.hashedId"
                        >
                          <p
                            v-if="
                              position.positionName ||
                              position.startMonth ||
                              position.startYear ||
                              position.endMonth ||
                              position.endYear
                            "
                          >
                            <span v-if="position.positionName">{{
                              position.positionName
                            }}</span>
                            <span
                              v-if="
                                position.positionName &&
                                (position.startMonth ||
                                  position.startYear ||
                                  position.endMonth ||
                                  position.endYear)
                              "
                            >
                              /
                            </span>
                            <span
                              v-if="
                                position.startMonth ||
                                position.startYear ||
                                position.endMonth ||
                                position.endYear
                              "
                            >
                              <span v-if="position.startMonth">{{
                                position.startMonth[0]
                              }}</span>
                              <span
                                v-if="position.startMonth && position.startYear"
                                >,
                              </span>
                              <span v-if="position.startYear">{{
                                position.startYear
                              }}</span>
                              &mdash;
                              <span
                                v-if="position.endMonth || position.endYear"
                              >
                                <span v-if="position.endMonth">{{
                                  position.endMonth[0]
                                }}</span>
                                <span
                                  v-if="position.endMonth && position.endYear"
                                  >,
                                </span>
                                <span v-if="position.endYear">{{
                                  position.endYear
                                }}</span>
                              </span>
                              <span v-else>
                                <span>Present</span>
                              </span>
                            </span>
                          </p>
                          <p v-if="position.description" class="text-sm mb-3">
                            {{ position.description }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section v-if="profile.links && profile.links.length > 0">
                <div
                  class="flex flex-wrap bg-white dark:bg-blueDianne-950 dark:text-white rounded-xl pb-5 pt-5 px-5 mt-10"
                >
                  <h2
                    class="text-3xl text-blueDianne dark:text-blueDianne-50 font-headline"
                  >
                    Links & Social Media
                  </h2>
                  <div
                    v-for="link in profile.links"
                    :key="link.hashedId"
                    class="w-full mt-5"
                  >
                    <a :href="link.url" target="_blank">
                      <h3 class="underline">
                        {{ link.description || "Link" }}
                      </h3>
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        <div class="w-full pt-2 mt-10">
          <div class="max-w-[1200px] mx-auto flex justify-between items-center">
            <h6
              class="px-5 py-3 inline-block text-white text-xl rounded-t-xl bg-tiara-600 dark:bg-blueDianne-950 ml-5 font-headline"
            >
              Join {{ profile.displayName }} on NotedSource!
            </h6>
            <a class="mr-5" :href="`${webBaseUrl}/auth/register`">
              <div
                class="-mt-10 flex items-center cursor-pointer rounded-lg bg-tiara-600 dark:bg-blueDianne-950 rounded-l-full rounded-b-full px-12 py-3"
              >
                <h6 class="text-white font-bold font-headline text-xl">
                  Join Now
                </h6>
              </div>
            </a>
          </div>
          <div class="bg-tiara-600 dark:bg-blueDianne-950">
            <div class="max-w-[1200px] mx-auto">
              <div class="px-5 py-10 text-sm">
                <p class="text-white mb-3">
                  At NotedSource, we believe that professors, post-docs,
                  scientists and other researchers have deep, untapped knowledge
                  and expertise that can be leveraged to drive innovation within
                  companies. NotedSource is committed to bridging the gap
                  between academia and industry by providing a platform for
                  collaboration with industry and networking with other
                  researchers.
                </p>

                <p class="text-white mb-3">
                  For industry, NotedSource identifies the right academic
                  experts in 24 hours to help organizations build and grow. With
                  a platform of thousands of knowledgeable PhDs, scientists, and
                  industry experts, NotedSource makes connecting and
                  collaborating easy.
                </p>

                <p class="text-white mb-3">
                  For academic researchers such as professors, post-docs, and
                  Ph.D.s, NotedSource provides tools to discover and connect to
                  your colleagues with messaging and news feeds, in addition to
                  the opportunity to be paid for your collaboration with vetted
                  partners.
                </p>
              </div>

              <div class="text-center">
                <h6 class="uppercase text-white">Expert Institutions</h6>
              </div>
              <div class="pb-4">
                <div class="flex flex-wrap items-center justify-center">
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-120-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      alt="NotedSource has experts from Stanford University"
                      src="/img/logo-stanford.png"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-120-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      alt="Expert institutions using NotedSource include Oxfort University"
                      src="/img/logo-oxford.webp"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-120-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      alt="Experts from McGill have used NotedSource to share their expertise"
                      src="/img/logo-mcgill.webp"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-120-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      alt="University of Chicago experts have used NotedSource"
                      src="/img/logo-chicago.svg"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-120-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      alt="MIT researchers have used NotedSource"
                      src="/img/logo-mit.webp"
                    />
                  </div>
                </div>
              </div>

              <div class="text-center mt-10">
                <h6 class="uppercase text-white">Proudly trusted by</h6>
              </div>
              <div class="pb-4">
                <div class="flex flex-wrap items-center justify-center">
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-100-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      src="/img/logo-microsoft.webp"
                      alt="Microsoft uses NotedSource for academic partnerships"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-100-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      src="/img/logo-johnson-and-johnson.webp"
                      alt="Johnson & Johnson academic research projects on NotedSource"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-100-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      src="/img/logo-proquest.png"
                      alt="ProQuest (Clarivate) uses NotedSource as their industry academia platform"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-100-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      src="/img/logo-slalom.png"
                      alt="Slamom consulting engages academics for research collaboration on NotedSource"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-100-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      src="/img/logo-omnicom-group.png"
                      alt="Omnicom and OMG find academics on notedsource"
                    />
                  </div>
                  <div class="p-4">
                    <img
                      class="max-h-100-px max-w-100-px"
                      style="filter: brightness(0) invert(1) opacity(0.5)"
                      src="/img/logo-unilever.png"
                      alt="Unilever research project have used NotedSource to engage academic experts"
                    />
                  </div>
                </div>
              </div>
              <p class="text-center pb-5">
                <a class="underline italic text-white/50" :href="baseUrl"
                  >Connect with researchers and scientists like
                  {{ profile.fullDisplayName }} on NotedSource to help your
                  company with innovation, research, R&D, L&D, and more.</a
                >
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { computed, defineAsyncComponent } from "vue";
import { useHead } from "@unhead/vue";
import { utils } from "common-frontend";
import { initializeApp } from "firebase/app";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import ProfileImage from "../components/ProfileImage.vue";
import RegularModal from "../components/Modals/Regular.vue";

const { keysToCamel, keysToSnake, imageUrlFromProfile } = utils;
const components = { ProfileImage, RegularModal };

if (!import.meta.env.SSR) {
  components.MarkdownViewer = defineAsyncComponent(
    () => import("../components/MarkdownViewer.vue"),
  );
  components.CreateCollaborationInquiry = defineAsyncComponent(
    () => import("../components/CreateCollaborationInquiry.vue"),
  );
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGE_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

initializeApp(firebaseConfig);
const db = getFirestore();

export default {
  components,
  async beforeRouteEnter(to) {
    const docRef = doc(
      db,
      "profile_caches",
      `${import.meta.env.VITE_FIREBASE_CACHE_PREFIX}--${
        to.params.slugOrHashedId
      }-PUBLIC`,
    );
    const docSnap = await getDoc(docRef);
    if (!docSnap.data()) {
      to.matched.forEach((toRouteMatched) => {
        toRouteMatched.props.notFound = true;
      });
    } else {
      to.matched.forEach((toRouteMatched) => {
        toRouteMatched.props.profile = keysToCamel(docSnap.data());
      });
    }
  },
  props: {
    showCollaborationInquiryModal: Boolean,
  },
  data() {
    return {
      loading: true,
      notFound: this.$route.matched[0].props.notFound,
      profile: this.$route.matched[0].props.profile,
      imageUrlFromProfile,
      baseUrl: import.meta.env.VITE_BASE_URL,
      webBaseUrl: import.meta.env.VITE_WEB_BASE_URL,
      cvBaseUrl: import.meta.env.VITE_CV_BASE_URL,
      isSsr: import.meta.env.SSR,
      isPrerendering: false,
      showingInquireModal: this.showCollaborationInquiryModal,
      isDark: null,
      initialTheme: null,
    };
  },
  watch: {
    $route(to) {
      this.showingInquireModal =
        to.matched.filter((toRouteMatched) => {
          return toRouteMatched.props.default.showCollaborationInquiryModal;
        }).length > 0;
    },
    isDark(newValue) {
      if (
        this.$refs.legacyMapIframe &&
        this.$refs.legacyMapIframe.contentWindow
      ) {
        this.$refs.legacyMapIframe.contentWindow.postMessage(
          { type: "theme_update", theme: newValue ? "dark" : "light" },
          "*",
        );
      }
    },
  },
  mounted() {
    if (!import.meta.env.SSR) {
      this.isPrerendering = window.navigator.userAgent.includes("Prerender");
      this.recordProfileView();

      this.isDark =
        localStorage.getItem("theme") === "dark" ||
        (!("theme" in localStorage) &&
          window.matchMedia("(prefers-color-scheme: dark)").matches);

      this.initialTheme = this.isDark ? "dark" : "light";

      // Listen for theme changes from the navbar
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === "class") {
            this.isDark = document.documentElement.classList.contains("dark");
          }
        });
      });
      observer.observe(document.documentElement, { attributes: true });
    }
    if (!this.notFound) {
      useHead({
        title: computed(
          () =>
            `Work with ${
              this.profile?.fullDisplayName || "Experts"
            } | NotedSource`,
        ),
        meta: [
          {
            name: `description`,
            content: computed(
              () =>
                this.profile?.about ||
                this.profile?.headline ||
                "Work with experts on NotedSource, a platform of thousands of knowledgeable PhDs, scientists, and industry experts",
            ),
          },
          {
            name: `og:description`,
            content: computed(
              () =>
                this.profile?.about ||
                this.profile?.headline ||
                "Work with experts on NotedSource, a platform of thousands of knowledgeable PhDs, scientists, and industry experts",
            ),
          },
        ],
      });
    }
  },
  methods: {
    recordProfileView() {
      if (this.$route.matched[0].props.profile) {
        const data = {
          profileId: this.$route.matched[0].props.profile.hashedId,
          visitorType: "PUBLIC",
          url: window.location.href,
          referer: window.document.referrer,
          userAgent: window.navigator.userAgent,
        };
        axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/record-profile-view/`,
          keysToSnake(data),
        );
      }
    },
  },
};
</script>
